import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [form, setForm] = useState({ name: '', email: '', interest: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Message sent successfully');
      setForm({ name: '', email: '', interest: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-white/15 focus:border-gold text-white placeholder-zinc-700 py-4 outline-none transition-colors duration-300 font-body text-sm";

  return (
    <section id="contact" className="relative py-28 md:py-40 px-6 md:px-12 lg:px-24" data-testid="cta-section">
      {/* Gold cinematic band */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background: 'linear-gradient(180deg, rgba(50,40,5,0) 0%, rgba(80,65,10,0.35) 15%, rgba(100,75,15,0.4) 50%, rgba(80,65,10,0.35) 85%, rgba(50,40,5,0) 100%)',
        }}
      />
      <div className="max-w-4xl mx-auto relative z-[1]" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase mb-6 leading-[0.95]">
            Be Part of <span className="text-gold">OZFEST</span>
          </h2>
          <p className="text-zinc-500 font-body font-light text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            Whether you want to partner, attend, or collaborate — we'd love to hear from you.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 25 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
          onSubmit={handleSubmit}
          className="space-y-8 max-w-lg mx-auto"
          data-testid="contact-form"
        >
          <input
            type="text"
            placeholder="Your Name"
            value={form.name}
            onChange={e => setForm({...form, name: e.target.value})}
            className={inputClass}
            required
            data-testid="contact-name"
          />
          <input
            type="email"
            placeholder="Email Address"
            value={form.email}
            onChange={e => setForm({...form, email: e.target.value})}
            className={inputClass}
            required
            data-testid="contact-email"
          />
          <select
            value={form.interest}
            onChange={e => setForm({...form, interest: e.target.value})}
            className={`${inputClass} appearance-none cursor-pointer`}
            data-testid="contact-interest"
          >
            <option value="" className="bg-black text-zinc-500">Area of Interest</option>
            <option value="partnership" className="bg-black">Partnership</option>
            <option value="attendance" className="bg-black">Attendance</option>
            <option value="sponsorship" className="bg-black">Sponsorship</option>
            <option value="media" className="bg-black">Media & Press</option>
            <option value="other" className="bg-black">Other</option>
          </select>
          <textarea
            placeholder="Your Message"
            value={form.message}
            onChange={e => setForm({...form, message: e.target.value})}
            rows={3}
            className={`${inputClass} resize-none`}
            data-testid="contact-message"
          />

          <div className="text-center pt-6">
            <button
              type="submit"
              disabled={submitting}
              className="btn-glow px-14 py-4 bg-gold text-black font-heading text-xs tracking-[0.25em] uppercase hover:bg-white transition-colors duration-300 disabled:opacity-50"
              data-testid="contact-submit"
            >
              {submitting ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
